import * as datGui from 'dat.gui'
import * as THREE from 'three'
import { Spherical, Vector3 } from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { ModelControls } from '../../utils/ModelControls'
import { CameraConstraints } from '../../../../../trillion-common/src/objects/camera-constraints.object'
import { setupMinMaxValueGUI } from './setupMinMaxValue.gui'
import { PointOfView } from '../../../../../trillion-common/src/objects/point-of-view.object'
import { StoreScheme } from '../../../shared/store/store'

export function addCameraGUI(
  gui: datGui.GUI,
  camera: THREE.PerspectiveCamera,
  controls: ModelControls | OrbitControls,
  scene: THREE.Scene,
  store: StoreScheme,
) {
  const setRotateObject = store.getState().setRotateObject
  const setIsFreeMoveCameraModeEnabled = store.getState().setIsFreeMoveCameraModeEnabled
  const setPointsOfView = store.getState().setPointsOfView
  const setAutoRotateSpeed = store.getState().setAutoRotateSpeed
  const defaultRotateSpeed = store.getState().autoRotateSpeed
  const viewerConfig = store.getState().viewerConfig
  let minDistance = 0.1
  let maxDistance = 10

  if (viewerConfig && viewerConfig.pointsOfView && viewerConfig.pointsOfView.length > 0) {
    const firstPointOfView = viewerConfig.pointsOfView[0]

    if (firstPointOfView.cameraConstraints) {
      minDistance = firstPointOfView.cameraConstraints.minDistance
      maxDistance = firstPointOfView.cameraConstraints.maxDistance
    }
  }

  setIsFreeMoveCameraModeEnabled(true)
  const cameraFolder = gui.addFolder('Camera')
  const freeMoveflag = cameraFolder
    .add(store.getState(), 'isFreeMoveCameraModeEnabled', false)
    .name('Free camera move')
    .onChange((value: boolean) => {
      setIsFreeMoveCameraModeEnabled(value)
    })

  cameraFolder
    .add(store.getState(), 'rotateObject')
    .name('Rotate object')
    .onChange((value: boolean) => {
      setRotateObject(value)
    })

  const autoRotateSpeedControl = createAutoRotateControls()
  store.subscribe(() => {
    freeMoveflag.updateDisplay()
    autoRotateSpeedControl.updateDisplay()
  })

  const objectsToUpdateOnCameraMove: datGui.GUIController[] = []
  const cameraSpheicalPosition = createCameraPositionControlElements()
  const { cameraTargetSphericalPosition, sphere } = createCameraTargetControlElements()
  createCameraConstraintsElements()

  controls.addEventListener('change', () => {
    cameraSpheicalPosition.setFromVector3(camera.position)
    cameraTargetSphericalPosition.setFromVector3(controls.target)
    sphere.position.copy(controls.target)
    objectsToUpdateOnCameraMove.forEach((object) => {
      object.updateDisplay()
    })
  })

  cameraFolder
    .add(camera, 'fov')
    .min(20)
    .max(170)
    .onChange(() => camera.updateProjectionMatrix())
  cameraFolder
    .add(camera, 'near')
    .min(0.001)
    .max(0.2)
    .step(0.001)
    .onChange(() => camera.updateProjectionMatrix())
  cameraFolder.add(camera, 'far').onChange(() => camera.updateProjectionMatrix())

  const buttons = createButtons()
  cameraFolder.add(buttons, 'savePositionAndTarget').name('Save position and target')
  cameraFolder.open()

  function createAutoRotateControls() {
    const autoRotateOption = { useAutoRotation: false }
    cameraFolder
      .add(autoRotateOption, 'useAutoRotation')
      .name('Use auto rotation')
      .onChange((value: boolean) => {
        if (value) {
          setAutoRotateSpeed(defaultRotateSpeed)
        } else {
          setAutoRotateSpeed(undefined)
        }
      })

    const autoRotateSpeedOption = { autoRotateSpeed: defaultRotateSpeed ?? 0.6 }
    const autoRotateSpeedControl = cameraFolder
      .add(autoRotateSpeedOption, 'autoRotateSpeed', 0.01, 3, 0.01)
      .name('Camera rotate speed')
      .onFinishChange((value: number) => {
        if (!autoRotateOption.useAutoRotation) return
        setAutoRotateSpeed(value)
        autoRotateSpeedOption.autoRotateSpeed = value
      })

    setAutoRotateSpeed(undefined)

    return autoRotateSpeedControl
  }

  function createButtons() {
    return {
      savePositionAndTarget: () => {
        const currentPointOfViewIndx = store.getState().currentPointOfViewIndx
        const pointsOfView = store.getState().pointsOfView
        const currentPointOfView = pointsOfView[currentPointOfViewIndx]
        currentPointOfView.cameraPosition = camera.position.clone()
        currentPointOfView.lookAtPosition = controls.target.clone()
        currentPointOfView.isFixedPointOfView = true
        adjustCameraConstraints(currentPointOfView)
        setPointsOfView([...pointsOfView])
      },
    }

    function adjustCameraConstraints(currentPointOfView: PointOfView) {
      if (currentPointOfView.cameraConstraints !== undefined) {
        const curDistance = controls.getDistance()
        if (curDistance < currentPointOfView.cameraConstraints.minDistance) {
          currentPointOfView.cameraConstraints.minDistance = minDistance
        }
        if (curDistance > currentPointOfView.cameraConstraints.maxDistance) {
          currentPointOfView.cameraConstraints.maxDistance = maxDistance
        }
        if (controls.getPolarAngle() < currentPointOfView.cameraConstraints.minPolarAngle) {
          currentPointOfView.cameraConstraints.minPolarAngle = controls.getPolarAngle()
        }
        if (controls.getPolarAngle() > currentPointOfView.cameraConstraints.maxPolarAngle) {
          currentPointOfView.cameraConstraints.maxPolarAngle = controls.getPolarAngle()
        }
        if (controls.getAzimuthalAngle() < currentPointOfView.cameraConstraints.minAzimuthAngle) {
          currentPointOfView.cameraConstraints.minAzimuthAngle = controls.getAzimuthalAngle()
        }
        if (controls.getAzimuthalAngle() > currentPointOfView.cameraConstraints.maxAzimuthAngle) {
          currentPointOfView.cameraConstraints.maxAzimuthAngle = controls.getAzimuthalAngle()
        }
      }
    }
  }

  function createCameraPositionControlElements() {
    const posFolder = cameraFolder.addFolder('Position')
    const cameraSpheicalPosition = new Spherical().setFromVector3(camera.position)
    addSphericalPosition(posFolder, 'Spherical', cameraSpheicalPosition, (spherical) => {
      camera.position.copy(new Vector3().setFromSpherical(spherical))
      camera.updateProjectionMatrix()
    }).open()
    addVector3Position(posFolder, 'Vector3', camera.position, () => camera.updateProjectionMatrix())
    return cameraSpheicalPosition
  }

  function createCameraConstraintsElements() {
    const cameraConstraintsFolder = cameraFolder.addFolder('Camera Constraints')

    const { currentValueGUI: polarAngleGUI, updateMinMaxDisplay: updateMinMaxPolar } =
      setupMinMaxValueGUI(
        cameraConstraintsFolder,
        'Polar angle (vertical)',
        0,
        Math.PI,
        (val) => {
          updateCameraConstraintsForCurrentPoint((cameraConstraints) => {
            cameraConstraints.minPolarAngle = val
          })
        },
        (val) => {
          updateCameraConstraintsForCurrentPoint((cameraConstraints) => {
            cameraConstraints.maxPolarAngle = val
          })
        },
        controls.getPolarAngle,
      )
    objectsToUpdateOnCameraMove.push(polarAngleGUI)

    const { currentValueGUI: azimuthalAngleGui, updateMinMaxDisplay: updateMinMaxAngle } =
      setupMinMaxValueGUI(
        cameraConstraintsFolder,
        'Azimuthal angle (horizontal)',
        -2 * Math.PI,
        2 * Math.PI,
        (val) => {
          updateCameraConstraintsForCurrentPoint((cameraConstraints) => {
            cameraConstraints.minAzimuthAngle = val
          })
        },
        (val) => {
          updateCameraConstraintsForCurrentPoint((cameraConstraints) => {
            cameraConstraints.maxAzimuthAngle = val
          })
        },
        controls.getAzimuthalAngle,
      )
    objectsToUpdateOnCameraMove.push(azimuthalAngleGui)

    const getDistanceWrapper = () => controls.getDistance()
    const { currentValueGUI: cameraTargetDistanceGui, updateMinMaxDisplay: updateMinMaxDistance } =
      setupMinMaxValueGUI(
        cameraConstraintsFolder,
        'Camera distance',
        minDistance,
        maxDistance,
        (val) => {
          updateCameraConstraintsForCurrentPoint((cameraConstraints) => {
            cameraConstraints.minDistance = val
          })
        },
        (val) => {
          updateCameraConstraintsForCurrentPoint((cameraConstraints) => {
            cameraConstraints.maxDistance = val
          })
        },
        getDistanceWrapper,
      )
    objectsToUpdateOnCameraMove.push(cameraTargetDistanceGui)

    const updateConstraintsGUIForPoint = (currentPointOfViewIndx: number) => {
      const currentPointOfView = store.getState().pointsOfView[currentPointOfViewIndx]
      const cameraConstraints = currentPointOfView.cameraConstraints
      if (cameraConstraints === undefined) return
      updateMinMaxPolar(cameraConstraints.minPolarAngle, cameraConstraints.maxPolarAngle)
      updateMinMaxAngle(cameraConstraints.minAzimuthAngle, cameraConstraints.maxAzimuthAngle)
      updateMinMaxDistance(cameraConstraints.minDistance, cameraConstraints.maxDistance)
    }

    store.subscribe((state) => state.currentPointOfViewIndx, updateConstraintsGUIForPoint)
    updateConstraintsGUIForPoint(store.getState().currentPointOfViewIndx)

    return cameraConstraintsFolder
  }

  function createCameraTargetControlElements() {
    const sphere = createSphere()
    scene.add(sphere)
    const rootFolder = cameraFolder.addFolder('Target')

    const cameraTargetSphericalPosition = new Spherical().setFromVector3(controls.target)
    addSphericalPosition(rootFolder, 'Spherical', cameraTargetSphericalPosition, (spherical) => {
      controls.target.copy(new Vector3().setFromSpherical(spherical))
      controls.update()
      sphere.position.copy(controls.target)
    }).open()
    addVector3Position(rootFolder, 'Vector3', controls.target, () => {
      controls.update()
      sphere.position.copy(controls.target)
    })
    return {
      cameraTargetSphericalPosition,
      sphere,
    }
  }

  function createSphere() {
    const geometry = new THREE.SphereGeometry(0.02)
    const material = new THREE.MeshBasicMaterial({
      color: 0xff0000,
    })
    const sphere = new THREE.Mesh(geometry, material)
    sphere.position.copy(controls.target)
    return sphere
  }

  function addSphericalPosition(
    rootFolder: datGui.GUI,
    folderName: string,
    spherical: Spherical,
    onChange: (spherical: Spherical) => void,
  ) {
    const targetFolder = rootFolder.addFolder(folderName)
    objectsToUpdateOnCameraMove.push(
      targetFolder.add(spherical, 'radius').onChange(() => onChange(spherical)),
    )
    objectsToUpdateOnCameraMove.push(
      targetFolder.add(spherical, 'phi').onChange(() => onChange(spherical)),
    )
    objectsToUpdateOnCameraMove.push(
      targetFolder.add(spherical, 'theta').onChange(() => onChange(spherical)),
    )
    return targetFolder
  }

  function addVector3Position(
    rootFolder: datGui.GUI,
    folderName: string,
    vector3: THREE.Vector3,
    onChange: (vector3: Vector3) => void,
  ) {
    const targetFolder = rootFolder.addFolder(folderName)
    objectsToUpdateOnCameraMove.push(
      targetFolder.add(vector3, 'x').onChange(() => onChange(vector3)),
    )
    objectsToUpdateOnCameraMove.push(
      targetFolder.add(vector3, 'y').onChange(() => onChange(vector3)),
    )
    objectsToUpdateOnCameraMove.push(
      targetFolder.add(vector3, 'z').onChange(() => onChange(vector3)),
    )
    return targetFolder
  }

  function updateCameraConstraintsForCurrentPoint(
    updateCallback: (cameraConstraints: CameraConstraints) => void,
  ) {
    const currentPointOfViewIndx = store.getState().currentPointOfViewIndx
    const pointsOfView = store.getState().pointsOfView
    if (currentPointOfViewIndx === -1 || pointsOfView.length === 0) return
    const currentPointOfView = pointsOfView[currentPointOfViewIndx]
    if (currentPointOfView.cameraConstraints === undefined)
      currentPointOfView.cameraConstraints = { ...store.getState().defaultCameraConstraints }
    updateCallback(currentPointOfView.cameraConstraints)
    pointsOfView[currentPointOfViewIndx] = currentPointOfView
    store.setState({ pointsOfView: [...pointsOfView] })
  }
}
